// Osa Search
import osa from "@wk/osa";
import Context = osa.common.Context;
import { DocumentResult, ExecuteQuery, ExecuteQueryResponse, SearchOsaService } from "@wk/osa-search-odata3";

// Types
import { FiltersInterface, ResultQuery, Sort } from "interfaces/searchPage/osaSearch";
import { Category } from "interfaces/searchPage/category";

// Constants
import { ReturnFields } from "constants/searchPage/gpoSearchEnum";
import { NUMBER_ELEMENTS_SUGGESTION } from "constants/searchPage/results";
import { suggestionQueryParams } from "constants/searchPage/apiQueryParam";

// Utility
import { buildQuery, buildQueryOnlyTags, getPropertyValues } from "services/osaSearch/utilityQueryGpoSearch";
import { formatPrice } from "utils/searchPage/textFormat";
import { removeParamsFromUrl } from "utils/searchPage/queryString";

interface OsaConfig {
	url: string;
}

export const getOsaContext = () => {
	const osaConfig: OsaConfig = {
		url: process.env.NEXT_PUBLIC_GPO_SEARCH_URL || ""
	};
	const osaSearchContext = new Context(osaConfig);
	return new SearchOsaService(osaSearchContext);
};

export const commonOsaQuery = async (querySearch: string, gpoFilters: FiltersInterface[], gpoSorting: Sort[], paging: number, productPerPage: number, categories: Category[]) => {
	const osaContext = getOsaContext();
	const request = new ExecuteQuery(buildQuery(querySearch, gpoFilters, gpoSorting, paging, productPerPage));
	const response = await osaContext.executeQuery(request);
	return buildSuggestionsSearchResultResponse(response, categories);
};

export const commonOsaQueryOnlyTags = async (osaContext: any, activeFilters: FiltersInterface[], categories: Category[], querySearch: string) => {
	const request = new ExecuteQuery(buildQueryOnlyTags(querySearch, activeFilters));
	const response = await osaContext.executeQuery(request);
	return buildSuggestionsSearchResultResponseOnlyTags(response, categories);
}

export const executeSuggestionsQuery = async (querySearch: string, categories: Category[]) => {
	const { filters, sorting, pageOffset } = suggestionQueryParams;
	return commonOsaQuery(querySearch, filters, sorting, pageOffset, NUMBER_ELEMENTS_SUGGESTION, categories);
};

export const buildSuggestionsSearchResultResponse = (response: ExecuteQueryResponse, categories: Category[]) => {
	if (response.rows > 0) {
		const documents = response.results.pop()?.documents;
		if (documents && documents.length > 0) {
			const resultQuery = buildResultsQuery(documents, categories);
			const idsTag = response.facets.find((f) => f.fieldName === ReturnFields.TAG_ID)?.facetResults.map((idTag) => +idTag.label);
			const allTags = idsTag ? categories.flatMap((cat) => cat.tags.filter((tag) => idsTag?.includes(tag.id))) : [];

			return {
				totalRows: response.rows,
				currentRows: resultQuery.length,
				results: resultQuery,
				tags: allTags
			};
		}
	}
};

export const buildSuggestionsSearchResultResponseOnlyTags = (response: ExecuteQueryResponse, categories: Category[]) => {
	if (response.rows > 0) {
			const idsTag = response.facets.find((f) => f.fieldName === ReturnFields.TAG_ID)?.facetResults.map((idTag) => +idTag.label);
			const allTags = idsTag ? categories.flatMap((cat) => cat.tags.filter((tag) => idsTag?.includes(tag.id))) : [];

			return {
				totalRows: response.rows,
				currentRows: 0,
				results: [],
				tags: allTags
			};
		}
};

const buildResultsQuery = (documents: DocumentResult[], categories: Category[]) => {
	const resultQuery = documents.map((doc) => {
		const idsTag = getPropertyValues(doc, ReturnFields.TAG_ID).map((idTag) => +idTag);
		const tags = categories.flatMap((cat) => cat.tags.filter((tag) => idsTag.includes(tag.id)));
		return {
			documentId: doc.documentId,
			title: getPropertyValues(doc, ReturnFields.TITLE).pop(),
			abstract: getPropertyValues(doc, ReturnFields.ABSTRACT).pop(),
			duration: getPropertyValues(doc, ReturnFields.SUBTITLE).pop(),
			locations: getPropertyValues(doc, ReturnFields.VENUES),
			startDateForLocations: getPropertyValues(doc, ReturnFields.VENUES_START_TIME),
			endDateForLocations: getPropertyValues(doc, ReturnFields.VENUES_END_TIME),
			grouping: getPropertyValues(doc, ReturnFields.PRODUCT_STATE).pop(),
			regularPrice: formatPrice(getPropertyValues(doc, ReturnFields.REGULAR_PRICE).pop() || "0"),
			price: formatPrice(getPropertyValues(doc, ReturnFields.MINIMUM_PRICE).pop() || "0"),
			productType: getPropertyValues(doc, ReturnFields.PRODUCT_TYPE).pop(),
			tags,
			urlProduct: getPropertyValues(doc, ReturnFields.PAGE_URL).pop(),
			imageUrl: removeParamsFromUrl(getPropertyValues(doc, ReturnFields.IMAGE_URL).pop() || "", "tipo"),
			listingLineDescription: getPropertyValues(doc, ReturnFields.LISTING_LINE_DESCRIPTION).pop()
		} as ResultQuery;
	});
	return resultQuery;
};
